import type { SyntheticEvent, CSSProperties, MouseEvent } from 'react';
import { useState } from 'react';
import { Confirm, Button, Dropdown } from 'semantic-ui-react';
import type {
  SemanticICONS,
  SemanticSIZES,
  StrictButtonProps,
  StrictDropdownItemProps,
  StrictPopupProps,
} from 'semantic-ui-react';
import GenericPopup from 'components/generic/GenericPopup';
import styled from 'styled-components';
import useWindowSize from 'hooks/useWindowSize';
import lang, { composeActionLabel } from 'vars/lang';
import type { FunctionalComponent } from 'apps/core/types/global.types';

export type GenericButtonProps = {
  content?: string | false;
  withPrefix?: boolean;
  icon?: SemanticICONS | boolean;
  onClick?: ((e?: MouseEvent) => void) | (() => Promise<void>);
  confirm?: boolean;
  confirmContent?: string;
  type: GenericButtonTypes;
  muted?: boolean;
  size?: SemanticSIZES;
  disabled?: boolean;
  loading?: boolean;
  style?: CSSProperties;
  stopPropagation?: boolean;
  popup?: StrictPopupProps | false;
  datatestid?: string;
  circular?: boolean;
  border?: boolean;
  className?: string;
  allowed?: boolean;
  attach?: 'left' | 'right';
  fluid?: boolean;
  inverted?: boolean;
  externalUrl?: string;
  compact?: boolean;
  dropdown?: {
    options: StrictDropdownItemProps[];
  };
};

const css = `
  &:last-child { margin-right: initial!important }
  white-space: nowrap;
`;

const StyledGenericButton = styled(Button)`
  ${css}
  ${(props) =>
    props.icon && !props.content && !props.$border
      ? `&.ui.button.icon { 
    box-shadow: none!important; border: 0!important;
    &:hover { box-shadow: none!important; border: 0!important; background: unset !important;}
    &:active { background: unset !important; }
    &:focus { background: unset !important; }
    margin-right: 0.2em!important;
  }`
      : 'margin-right: 0.7em!important;'}
  ${(props) =>
    props.$attach === 'left' &&
    `
    &.ui.button {
      border-top-right-radius: 0px;
      border-bottom-right-radius: 0px;
    }
  `};
  ${(props) =>
    props.$attach === 'right' &&
    `
  &.ui.button {
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;
  }
`};
`;

const StyledButtonContainer = styled.div`
  ${css}
  display: inline;
`;

export enum GenericButtonTypes {
  DELETE = 'DELETE',
  ADD = 'ADD',
  SAVE = 'SAVE',
  SAVED = 'SAVED',
  UNDO = 'UNDO',
  CANCEL = 'CANCEL',
  BACK = 'BACK',
  HISTORY = 'HISTORY',
  DISABLE = 'DISABLE',
  EXPORT = 'EXPORT',
  IMPORT = 'IMPORT',
  EDIT = 'EDIT',
  CONFIGURE = 'CONFIGURE',
  UPDATE = 'UPDATE',
  REFRESH = 'REFRESH',
  MINIMIZE = 'MINIMIZE',
  SEARCH = 'SEARCH',
  LIGHT_MODE = 'LIGHT_MODE',
  DARK_MODE = 'DARK_MODE',
  DROPDOWN = 'DROPDOWN',
  DOWNLOAD = 'DOWNLOAD',
  COPY = 'COPY',
  NEXT = 'NEXT',
  APPROVE = 'APPROVE',
  DENY = 'DENY',
  MATCH = 'MATCH',
  GENERATE = 'GENERATE',
  RESET = 'RESET',
  EXTERNAL = 'EXTERNAL',
  FAVORITE = 'FAVORITE',
  VIEW = 'VIEW',
  CLOSE = 'CLOSE',
}

const DefaultButtonAttributes: {
  [key in GenericButtonTypes]: {
    content: string;
    tooltip?: StrictPopupProps;
    icon?: SemanticICONS;
    color?: string;
  };
} = {
  ADD: {
    content: lang.addItem,
    icon: 'plus',
    color: 'teal',
  },
  APPROVE: {
    content: lang.generics.approve,
    icon: 'check circle',
    color: 'green',
  },
  DENY: {
    content: lang.generics.deny,
    icon: 'x',
    color: 'red',
  },
  SAVE: {
    content: lang.saveItem,
    icon: 'plus',
    color: 'green',
  },
  SAVED: {
    content: lang.saved,
    icon: 'checkmark',
    color: 'green',
  },
  UNDO: {
    content: lang.undoItem,
    icon: 'close',
  },
  BACK: {
    content: lang.back,
    icon: 'arrow left',
  },
  CANCEL: {
    content: lang.cancelItem,
    icon: 'close',
  },
  DELETE: {
    content: lang.deleteItem,
    icon: 'close',
    color: 'red',
  },
  HISTORY: {
    content: lang.generics.history,
    tooltip: {
      content: composeActionLabel(lang.showItem, lang.generics.history, ''),
    },
    icon: 'history',
  },
  DISABLE: {
    content: lang.disableItem,
    icon: 'close',
    color: 'orange',
  },
  EXPORT: {
    content: lang.exportItem,
    icon: 'upload',
  },
  IMPORT: {
    content: lang.importItem,
    icon: 'download',
  },
  EDIT: {
    content: 'Edit',
    icon: 'edit',
  },
  CONFIGURE: {
    content: lang.configureItem,
    icon: 'cog',
  },
  UPDATE: {
    content: lang.updateItem,
    icon: 'check',
    color: 'green',
  },
  REFRESH: {
    content: 'Refresh',
    icon: 'redo alternate',
  },
  DROPDOWN: {
    content: 'Select Actions',
    icon: 'ellipsis vertical',
  },
  DOWNLOAD: {
    content: 'Download',
    icon: 'download',
  },
  MINIMIZE: {
    content: lang.minimizeItem,
    icon: 'minus',
  },
  LIGHT_MODE: {
    content: 'Light',
    icon: 'sun',
  },
  DARK_MODE: {
    content: 'Dark',
    icon: 'moon',
  },
  COPY: {
    content: 'Copy',
    icon: 'copy outline',
  },
  SEARCH: {
    content: 'Search',
    icon: 'search',
  },
  NEXT: {
    content: lang.next,
    icon: 'arrow right',
    color: 'teal',
  },
  MATCH: {
    content: composeActionLabel(lang.matchItem, ''),
    icon: 'exchange',
  },
  GENERATE: {
    content: lang.generics.generate,
    icon: 'sync',
  },
  RESET: {
    content: lang.resetItem,
    icon: 'undo',
  },
  EXTERNAL: {
    content: 'URL',
    icon: 'external alternate',
  },
  FAVORITE: {
    content: 'Favorite',
    icon: 'star',
    tooltip: { content: 'Favorite Searches' },
  },
  VIEW: {
    content: 'View',
    icon: 'eye',
  },
  CLOSE: {
    content: lang.generics.close,
    icon: 'close',
  },
};

const GenericButton: FunctionalComponent<GenericButtonProps> = ({
  content,
  withPrefix = true,
  icon,
  onClick,
  confirm,
  confirmContent,
  type,
  muted,
  size,
  disabled,
  loading,
  style,
  stopPropagation = false,
  popup,
  datatestid,
  circular,
  border = true,
  className,
  allowed = true,
  attach,
  fluid,
  inverted,
  externalUrl,
  compact,
  dropdown,
}) => {
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const { isMobile } = useWindowSize();

  if (!allowed) return null; // Return early if not allowed

  withPrefix = isMobile ? false : withPrefix; // Remove prefix on mobile to save space

  const isSmall = isMobile || (size && ['tiny', 'mini'].includes(size));

  const isIconButton = !content && !border;

  const buttonContent = (): StrictButtonProps['content'] => {
    if (content === false) {
      return undefined;
    }
    if (content) {
      if (withPrefix) return composeActionLabel(DefaultButtonAttributes[type].content, content);
      return content;
    }
    return composeActionLabel(DefaultButtonAttributes[type]?.content, '');
  };

  const buttonIcon = (): StrictButtonProps['icon'] => {
    if (icon === false) {
      return false;
    }
    if (typeof icon === 'string') {
      return icon;
    }
    return DefaultButtonAttributes[type].icon;
  };

  const buttonColor = !muted && DefaultButtonAttributes[type]?.color;
  const buttonHtmlType = type === GenericButtonTypes.SAVE ? 'submit' : 'button';
  const buttonTooltip = DefaultButtonAttributes[type].tooltip;

  const buttonLabelPosition = (): StrictButtonProps['labelPosition'] => {
    if (isSmall) return undefined;
    if (buttonIcon() && buttonContent()) return 'left';
  };

  // When button is an icon only
  if (!buttonContent() && buttonIcon()) {
    circular = circular ?? true;
    size = border && !size ? 'small' : size;
    if (popup !== false) {
      popup = {
        content: composeActionLabel(DefaultButtonAttributes[type]?.content, ''),
        ...buttonTooltip,
        ...popup,
      };
    }
  }

  const onButtonClick = (e: MouseEvent) => {
    if (externalUrl) window.open(externalUrl);
    if (onClick) void onClick(e);
  };

  let button = (
    <>
      <StyledGenericButton
        className={className}
        basic
        content={buttonContent()}
        icon={buttonIcon()}
        color={!isIconButton ? buttonColor : undefined}
        labelPosition={buttonLabelPosition()}
        onClick={(e: MouseEvent) => {
          if (stopPropagation) e.stopPropagation();
          if (confirm) setIsConfirming(true);
          else onButtonClick(e);
        }}
        size={size}
        loading={loading}
        disabled={disabled}
        type={buttonHtmlType}
        style={style}
        data-testid={datatestid}
        circular={circular}
        fluid={fluid}
        compact={compact}
        inverted={inverted}
        $border={border}
        $attach={attach}
      />
      {confirm && (
        <Confirm
          open={isConfirming}
          onCancel={(event: SyntheticEvent) => {
            if (stopPropagation) event.stopPropagation();
            setIsConfirming(false);
          }}
          content={confirmContent}
          onConfirm={(event: MouseEvent) => {
            if (stopPropagation) event.stopPropagation();
            onButtonClick(event);
            setIsConfirming(false);
          }}
        />
      )}
    </>
  );

  if (dropdown) {
    button = <Dropdown icon={false} trigger={button} options={dropdown.options} />;
  }

  return (!buttonContent() && popup !== false) || popup ? (
    <GenericPopup
      trigger={<StyledButtonContainer>{button}</StyledButtonContainer>}
      position="top center"
      {...popup}
    />
  ) : (
    button
  );
};

export default GenericButton;
